import '.a{display:flex;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.v{background-color:var(--palette-white);}.ssr-plugin'
import '.jw{border-radius:var(--border-radius-image);}.ssr-plugin'
import '.jx{padding:3.5rem 2rem;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.ar{text-align:center;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.bc{height:100%;}.ssr-plugin'
import '.in{box-sizing:border-box;}.ssr-plugin'
import '.jy{width:12.5rem;}.ssr-plugin'
import '.h{display:grid;}.ssr-plugin'
import '.jz{grid-template-rows:auto 1fr auto;}.ssr-plugin'
import '.bd{flex:1;}.ssr-plugin'
import '.aq{justify-content:center;}.ssr-plugin'
import '.k0{padding:0.75rem 0;}.ssr-plugin'
import '.b1{justify-content:flex-end;}.ssr-plugin'
import '.s{flex-direction:row;}.ssr-plugin'
import '.d{gap:1rem;}.ssr-plugin'
import '.at{flex-shrink:0;}.ssr-plugin'
import '.ao{position:relative;}.ssr-plugin'
import '.k1{transition:transform 0.25s ease, box-shadow 0.25s ease;}.ssr-plugin'
import '.k2{border:var(--card-border);}.ssr-plugin'
import '.k3{background-color:lightgray;}.ssr-plugin'
import '.k4{background-size:100px 100px, auto;}.ssr-plugin'
import '.k5{background-repeat:repeat, no-repeat;}.ssr-plugin'
import '.k6{background-position:0% 0%, 0% 0%;}.ssr-plugin'
import '.k7{background-blend-mode:overlay, normal;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { Image } from '@app/components/core/image/Image'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { Title } from '@app/components/core/typography/Title'
import useMediaQuery from '@app/util/useMediaQuery'
import contactCardTexture from '@app/assets/images/background-contact-card.png'
import { type Style, styled } from 'inlines'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'

export type ContactPerson = {
  imageSrc: string
  imageAlt: string
  name: string
  role: string
  buttonLabel: string
  buttonLink: string
}

export type ContactCardProps = {
  title: string
  description?: string
  htmlContent?: string
  contactPeople: ContactPerson[]
  style?: Style
}

const CONTACT_PERSON_CARD_WIDTH = '16.5rem'
const STACK_PEEK_AT_THREE = '10rem'
const STACK_PEEK_AT_FIVE = '3rem'
const STACK_COUNT_FOR_WIDEST_PEEK = 3
const STACK_COUNT_FOR_TIGHTEST_PEEK = 5

const getHorizontalStackPeek = (count: number): string => {
  if (count <= STACK_COUNT_FOR_WIDEST_PEEK) {
    return STACK_PEEK_AT_THREE
  }

  if (count >= STACK_COUNT_FOR_TIGHTEST_PEEK) {
    return STACK_PEEK_AT_FIVE
  }

  const minPeek = parseFloat(STACK_PEEK_AT_THREE)
  const maxPeek = parseFloat(STACK_PEEK_AT_FIVE)
  const progress =
    (count - STACK_COUNT_FOR_WIDEST_PEEK) /
    (STACK_COUNT_FOR_TIGHTEST_PEEK - STACK_COUNT_FOR_WIDEST_PEEK)
  const peek = minPeek + progress * (maxPeek - minPeek)

  return `${peek}rem`
}

const getHorizontalStackOverlap = (peek: string) =>
  `(${CONTACT_PERSON_CARD_WIDTH} - ${peek})`

const ContactPersonCard = ({
  person,
  style,
}: {
  person: ContactPerson
  style?: Style
}) => (
  <styled.div className="a b v jw jx t ar n bc in"
    style={{
      ...style,
    }}
  >
    <Image
      src={person.imageSrc}
      alt={person.imageAlt}
      resizeWidth={144}
      style={{
        width: '9rem',
        height: '9rem',
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: 'center top',
        marginBottom: '0.75rem',
      }}
    />
    <div className="jy h jz bd"
    >
      <Title.H4
        style={{
          whiteSpace: 'pre-line',
          marginBottom: 0,
        }}
      >
        {person.name}
      </Title.H4>
      <div className="a t aq ar k0"
      >
        <Paragraph.Medium
          style={{
            marginBottom: 0,
          }}
        >
          {person.role}
        </Paragraph.Medium>
      </div>
      {person.buttonLabel.trim() ? (
        <Button
          size="medium"
          variant="primary"
          label={person.buttonLabel}
          to={person.buttonLink || undefined}
        />
      ) : null}
    </div>
  </styled.div>
)

const ContactPeopleLayout = ({
  contactPeople,
  isMobile,
}: {
  contactPeople: ContactPerson[]
  isMobile: boolean
}) => {
  const count = contactPeople.length

  if (count === 0) {
    return null
  }

  if (isMobile) {
    return (
      <Column>
        {contactPeople.map((person, index) => (
          <ContactPersonCard
            key={`${index}-${person.name}`}
            person={person}
          />
        ))}
      </Column>
    )
  }

  if (count === 1) {
    return (
      <div className="a b1"
        style={{
          width: CONTACT_PERSON_CARD_WIDTH,
        }}
      >
        <ContactPersonCard person={contactPeople[0]} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div className="a s d b1"
      >
        {contactPeople.map((person, index) => (
          <div className="at"
            key={`${index}-${person.name}`}
            style={{
              width: CONTACT_PERSON_CARD_WIDTH,}}
          >
            <ContactPersonCard person={person} />
          </div>
        ))}
      </div>
    )
  }

  const stackPeek = getHorizontalStackPeek(count)
  const stackOverlap = getHorizontalStackOverlap(stackPeek)

  return (
    <div className="a s b1"
      style={{
        width: `calc(${CONTACT_PERSON_CARD_WIDTH} + ${count - 1} * ${stackPeek})`,
      }}
    >
      {contactPeople.map((person, index) => (
        <styled.div className="ao at k1 jw k2"
          key={`${index}-${person.name}`}
          style={{
            width: CONTACT_PERSON_CARD_WIDTH,
            marginLeft: index === 0 ? 0 : `calc(-1 * ${stackOverlap})`,
            zIndex: index + 1,
            '&:hover': {
              zIndex: count + 1,
              transform: 'translateY(-0.5rem)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
            },
          }}
        >
          <ContactPersonCard person={person} />
        </styled.div>
      ))}
    </div>
  )
}

export const ContactCard = ({
  title,
  description,
  htmlContent,
  style,
  contactPeople,
}: ContactCardProps) => {
  const isMobile = useMediaQuery()

  return (
    <styled.div className="bc k3 k4 k5 k6 k7"
      style={{
        backgroundImage: `url(${contactCardTexture}), linear-gradient(177deg, #FFF 13.41%, #FBEBD1 66.89%, #FFCB93 93.89%, #F1A782 115.68%)`,
        borderRadius: isMobile ? '0.5rem' : '3rem',
        padding: isMobile ? '0.5rem' : '4.5rem',
        ...style,
      }}
    >
      <div className="h t"
        style={{
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
          gap: isMobile ? '2rem' : '9rem',}}
      >
        <styled.div>
          {title && <Title.H2>{title}</Title.H2>}
          {description && <Paragraph.Medium>{description}</Paragraph.Medium>}
          {htmlContent && <SafeHtml html={htmlContent} />}
        </styled.div>

        <ContactPeopleLayout
          contactPeople={contactPeople}
          isMobile={isMobile}
        />
      </div>
    </styled.div>
  )
}
